<template>
    <div class="meeting-detail-page">
        <div class="page-card">

            <!-- ================= 1. 顶部：会议基础信息 ================= -->
            <div class="header-section">
                <div class="meeting-icon">
                    <a-icon type="calendar" style="font-size: 24px; color: #1890ff;" />
                </div>
                <div class="meeting-info">
                    <div class="meeting-title">
                        {{ detail.title }}
                        <a-tooltip :title="'会议ID: ' + detail.id">
                            <a-icon type="info-circle" style="color: #faad14; margin-left: 8px; cursor: pointer;" />
                        </a-tooltip>
                    </div>
                    <div class="meeting-meta">
                        {{ formatDate(detail.startTime) }} - {{ formatDate(detail.endTime) }} | {{ detail.meetRoom ? detail.meetRoom.name + ' (' + detail.meetRoom.location + detail.meetRoom.floor + ')' : '' }}
                    </div>
                </div>
            </div>

            <a-divider style="margin: 16px 0;" />

            <!-- ================= 2. 参会人员确认 ================= -->
            <div class="section-block">
                <div class="section-header">
                    <div class="section-title"><span class="bar"></span>确认实际到会人员</div>
                    <div class="section-actions">
                        <span class="stats-text">已到会 {{ attendeeList.filter(a => a.attendance === 'present').length }} / 未到会 {{ attendeeList.filter(a => a.attendance !== 'present').length }} / 共 {{ attendeeList.length }} 人</span>
                        <a-switch checked-children="全部已到" un-checked-children="全部已到" :checked="allAttended" @change="onAllAttendedChange" />
                    </div>
                </div>

                <div class="attendee-table-wrapper">
                    <a-table :dataSource="attendeeList" :pagination="false" rowKey="id" bordered size="middle">
                        <a-table-column title="姓名" dataIndex="name" key="name">
                            <template slot-scope="text, record">
                                <div class="user-cell">
                                    <div class="avatar-circle" :style="{ background: record.color }">{{ text.charAt(0) }}</div>
                                    <span>{{ text }}</span>
                                </div>
                            </template>
                        </a-table-column>
                        <a-table-column title="部门" dataIndex="department" key="department"  align="center"/>
                        <a-table-column title="角色" dataIndex="role" key="role"  align="center"/>

                        <a-table-column title="响应状态" key="responseStatus" align="center">
                            <template slot-scope="text, record">
                                <span class="status-tag" :class="record.responseClass">{{ record.responseStatus }}</span>
                            </template>
                        </a-table-column>

                        <a-table-column title="到会状态" key="attendance" align="center">
                            <template slot-scope="text, record">
                                <a-select
                                    :value="record.attendance"
                                    size="small"
                                    style="width: 110px"
                                    @change="val => toggleAttendance(record.id, val)"
                                >
                                    <a-select-option v-for="opt in attendanceOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.label }}
                                    </a-select-option>
                                </a-select>
                            </template>
                        </a-table-column>
                    </a-table>
                </div>
            </div>

            <a-divider style="margin: 24px 0;" />

            <!-- ================= 3. 会议纪要上传 ================= -->
            <div class="section-block">
                <div class="section-header">
                    <div class="section-title"><span class="bar"></span>会议纪要上传</div>
                </div>

                <div class="upload-area">
                    <div class="upload-trigger-row">
                        <a-button type="primary" icon="upload" @click="triggerFileInput">附件上传</a-button>
                        <input type="file" ref="fileInput" @change="handleFileChange" accept=".pdf,.docx,.doc,.jpg,.jpeg,.png" multiple style="display: none" />
                        <span class="upload-tip">支持 PDF、DOCX、JPG，单个文件不超过 20MB</span>
                    </div>

                    <!-- 已上传文件列表 -->
                    <div class="file-list" v-if="uploadFileList.length">
                        <div class="file-item file-list" v-for="(file, index) in uploadFileList" :key="index">
                            <div class="file-info">
                                <a-icon :type="fileIcon(file.name)" style="font-size: 18px; margin-right: 8px;" :style="{ color: fileIconColor(file.name) }" />
                                <a v-if="file.completePath" :href="file.completePath" target="_blank" class="file-name">{{ file.name }}</a>
                                <span v-else class="file-name">{{ file.name }}</span>
                                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                            </div>
                            <a-icon type="delete" class="delete-icon" style="color: #ff4d4f;" @click="removeFile(index)" />
                        </div>
                    </div>

<!--                    <div class="notify-checkbox">-->
<!--                        <a-checkbox>通知所有参会人员</a-checkbox>-->
<!--                    </div>-->
                </div>
            </div>

            <a-divider style="margin: 24px 0;" />

            <!-- ================= 4. 督办快速发起 ================= -->
            <div class="section-block last-section">
                <div class="section-header">
                    <div class="section-title"><span class="bar"></span>督办快速发起</div>
                    <a-button type="primary" icon="plus" @click="toggleSuperviseForm">新增督办</a-button>
                </div>

                <!-- 督办新增/编辑表单 -->
                <div class="supervise-form-wrapper" v-if="showSuperviseForm">
                    <a-form :form="superviseForm" layout="vertical">
                        <a-row :gutter="24">
                            <a-col :span="16">
                                <a-form-item label="督办事项" required>
                                    <a-input v-decorator="['taskName', { rules: [{ required: true, message: '请输入督办事项' }] }]" placeholder="请输入督办事项..." />
                                </a-form-item>
                            </a-col>
                            <a-col :span="8">
                                <a-form-item label="办结时限" required>
                                    <a-date-picker v-decorator="['deadline', { rules: [{ required: true, message: '请选择截止日期' }] }]" placeholder="选择截止日期" style="width: 100%;" />
                                </a-form-item>
                            </a-col>
                        </a-row>

                        <a-row :gutter="24">
                            <a-col :span="8">
                                <a-form-item label="督办类型" required>
                                    <a-cascader
                                        :options="typeList"
                                        placeholder="选择类型"
                                        :fieldNames="{ label: 'name', value: 'value', children: 'children' }"
                                        v-decorator="['type', { rules: [{ required: true, message: '请选择类型' }] }]"
                                        style="width: 100%"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :span="8">
                                <a-form-item>
                                    <span slot="label" class="required">执行人</span>
                                    <div v-if="executorList.length > 0">
                                        <div class="selected-user" v-for="(user, index) in executorList" :key="user.userId">
                                            <a-button block style="text-align: left">
                                                <div v-if="user.name">
                                                    {{ user.deptUniqueName }} - {{ user.name }}
                                                </div>
                                                <div v-else>&nbsp;</div>
                                            </a-button>
                                            <EmployeeSelector
                                                :single="false"
                                                v-if="index === executorList.length - 1"
                                                @change="(arr) => { executorList = arr }"
                                                :value="executorList"
                                            >
                                                <a-button size="small" type="primary" shape="circle" icon="plus" style="position: absolute; top: 8px; right: -32px"></a-button>
                                            </EmployeeSelector>
                                            <a-button
                                                size="small" type="danger" shape="circle" icon="minus"
                                                :style="{ position: 'absolute', top: '8px', right: index !== executorList.length - 1 ? '-32px' : '-62px' }"
                                                @click="removeUser(index)"
                                            ></a-button>
                                        </div>
                                    </div>
                                    <div class="selected-user" v-else>
                                        <EmployeeSelector
                                            :single="false"
                                            @change="(arr) => { executorList = arr }"
                                            :value="executorList"
                                        >
                                            <a-button block style="text-align: left"><div>&nbsp;</div></a-button>
                                            <a-button size="small" type="primary" shape="circle" icon="plus" style="position: absolute; top: 8px; right: -32px"></a-button>
                                        </EmployeeSelector>
                                    </div>
                                </a-form-item>
                            </a-col>
                        </a-row>

                        <a-row :gutter="24">
                            <a-col :span="24">
                                <a-form-item label="办结要求">
                                    <a-textarea v-decorator="['requirements']" placeholder="请输入办结要求和验收标准..." :auto-size="{ minRows: 3, maxRows: 5 }" />
                                </a-form-item>
                            </a-col>
                        </a-row>

                        <div class="form-actions">
                            <a-button @click="toggleSuperviseForm" style="margin-right: 12px;">取消</a-button>
                            <a-button type="primary" @click="handleSaveSupervise" :loading="superviseLoading">保存督办</a-button>
                        </div>
                    </a-form>
                </div>

                <!-- 督办列表 -->
                <div class="supervise-list-wrapper">
                    <a-table :dataSource="superviseList" :columns="superviseColumns" rowKey="id" :pagination="false" bordered>
                        <template slot="action" slot-scope="text, record">
<!--                            <a @click="handleEditSupervise(record)">编辑</a>-->
<!--                            <a-divider type="vertical" />-->
                            <a style="color: #ff4d4f" @click="handleDeleteSupervise(record.id)">删除</a>
                        </template>
                    </a-table>
                </div>
            </div>

            <!-- 底部全局操作 -->
            <div class="footer-actions">
                <a-button @click="handleClose">关闭</a-button>
                <a-button type="primary" @click="handleSaveAll" :loading="saveLoading">保存</a-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { fetchMeetingDetail, archiveMeeting } from '@/api/meeting'
import { uploadFile } from '@/api/upload'
import EmployeeSelector from "@/components/employee-selector";
import moment from "moment";

export default {
    name: "MeetingDetail",
    components: { EmployeeSelector },
    data() {
        return {
            detail: {},
            loading: false,
            saveLoading: false,
            superviseList: [],
            deletedSuperviseIds: [],
            allAttended: false,
            attendanceState: {},
            attendanceOptions: [
                { value: 'present', label: '已到会' },
                { value: 'absent', label: '未到会' },
                { value: 'late', label: '迟到' },
                { value: 'leave_early', label: '早退' },
            ],

            // 督办表单控制
            showSuperviseForm: false,
            superviseLoading: false,
            superviseForm: this.$form.createForm(this),

            // 执行人
            executorList: [],
            editingId: null,

            // 文件上传
            uploadFileList: [],

            // 督办列表列定义
            superviseColumns: [
                { title: '督办事项', dataIndex: 'taskName', key: 'taskName' },
                { title: '办结时限', dataIndex: 'deadline', key: 'deadline' },
                { title: '督办类型', dataIndex: 'type', key: 'type', customRender: (text, record) => this.getTypeName(record.type) },
                { title: '督办子类型', dataIndex: 'subType', key: 'subType', customRender: (text, record) => this.getSubTypeName(record.subType) },
                { title: '执行人', dataIndex: 'executor', key: 'executor' },
                { title: '办结要求', dataIndex: 'requirements', key: 'requirements' },
                // {
                //     title: '操作', key: 'action', align: 'center',
                //     scopedSlots: { customRender: 'action' }
                // }
            ],
        };
    },
    computed: {
        ...mapGetters('setting', ['findSingleDictName', 'findDataDict']),
        typeList() {
            return this.findDataDict("dblx");
        },
        attendeeList() {
            return (this.detail.attendeeList || []).map(item => {
                const respMap = { confirmed: '已确认', pending: '待响应', leave_approved: '已请假', leave_pending: '请假待审批', leave_applying: '请假申请' };
                const respClassMap = { confirmed: 'status-confirmed', pending: 'status-pending', leave_approved: 'status-rejected', leave_pending: 'status-pending', leave_applying: 'status-blue' };
                return {
                    id: item.id,
                    name: item.userName,
                    department: item.deptName || '',
                    role: item.role || '参会人',
                    color: this.getAvatarColor(item.userName),
                    responseStatus: respMap[item.responseState] || item.responseState || '未响应',
                    responseClass: respClassMap[item.responseState] || 'status-pending',
                    attendance: this.attendanceState[item.id] || 'absent',
                };
            });
        },

    },
    mounted() {
        const id = this.$route.query.id || this.$route.params.id;
        if (id) {
            this.fetchDetail(id);
        }
    },
    methods: {
        async fetchDetail(id) {
            this.loading = true;
            try {
                const res = await fetchMeetingDetail({ id });
                const data = res;
                this.detail = data;
                const list = data.attendeeList || [];
                this.attendanceState = {};
                const validAttendanceStates = ['present', 'absent', 'late', 'leave_early'];
                list.forEach(item => {
                    const state = validAttendanceStates.includes(item.attendanceState) ? item.attendanceState : 'absent';
                    this.$set(this.attendanceState, item.id, state);
                });
                this.allAttended = list.every(item => item.attendanceState === 'present');
                this.uploadFileList = (data.minutesFiles || []).map(f => ({ ...f }));
                this.deletedSuperviseIds = [];
                this.superviseList = (data.superviseTasks || []).map(item => {
                    const assignees = item.assignees || (item.assigneeId ? [{ assigneeId: item.assigneeId, assigneeName: item.assigneeName, assigneeDeptId: item.assigneeDeptId, assigneeDeptName: item.assigneeDeptName }] : []);
                    return {
                        id: item.id,
                        taskName: item.name,
                        deadline: item.originalDate,
                        type: item.type,
                        subType: item.subType,
                        executor: assignees.map(u => u.assigneeName).join('、'),
                        executorList: assignees.map(u => ({ userId: u.assigneeId, name: u.assigneeName, deptId: u.assigneeDeptId, deptUniqueName: u.assigneeDeptName })),
                        requirements: item.remarks || '-',
                    };
                });
            } catch (e) {
                console.error('获取会议详情失败', e);
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}年${pad(d.getMonth() + 1)}月${pad(d.getDate())}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
        getAvatarColor(name) {
            if (!name) return '#1890ff';
            const colors = ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#faad14', '#13c2c2', '#f5222d', '#2f54eb'];
            let hash = 0;
            for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
            return colors[Math.abs(hash) % colors.length];
        },
        onAllAttendedChange(checked) {
            this.allAttended = checked;
            const state = checked ? 'present' : 'absent';
            Object.keys(this.attendanceState).forEach(id => {
                this.$set(this.attendanceState, id, state);
            });
        },
        toggleAttendance(id, state) {
            this.$set(this.attendanceState, id, state);
            const all = Object.values(this.attendanceState).every(v => v === 'present');
            this.allAttended = all;
        },
        toggleSuperviseForm() {
            this.showSuperviseForm = !this.showSuperviseForm;
            if (!this.showSuperviseForm) {
                this.superviseForm.resetFields();
                this.executorList = [];
                this.editingId = null;
            }
        },
        removeUser(index) {
            this.executorList.splice(index, 1);
            this.executorList = [...this.executorList];
        },
        getTypeName(value) {
            if (!value) return '-';
            return this.typeList?.find(item => item.value === value || item.name === value)?.name || value;
        },
        getSubTypeName(value) {
            if (!value) return '-';
            for (const parent of (this.typeList || [])) {
                const child = (parent.children || []).find(item => item.value === value || item.name === value);
                if (child) return child.name;
            }
            return value;
        },
        resolveTypeValue(type, subType) {
            for (const parent of (this.typeList || [])) {
                if (parent.value === type || parent.name === type) {
                    const child = (parent.children || []).find(item => item.value === subType || item.name === subType);
                    return { typeValue: parent.value, subTypeValue: child ? child.value : '' };
                }
            }
            return { typeValue: type || '', subTypeValue: subType || '' };
        },
        handleSaveSupervise() {
            this.superviseForm.validateFields((err, values) => {
                if (!err) {
                    if (this.executorList.length === 0) {
                        this.$message.error('请选择执行人');
                        return;
                    }
                    this.superviseLoading = true;
                    setTimeout(() => {
                        const typeValue = Array.isArray(values.type) ? values.type[0] : values.type;
                        const subTypeValue = Array.isArray(values.type) ? values.type[1] : undefined;
                        const taskData = {
                            taskName: values.taskName,
                            deadline: values.deadline.format('YYYY-MM-DD'),
                            type: typeValue,
                            subType: subTypeValue,
                            executor: this.executorList.map(u => u.name).join('、'),
                            executorList: this.executorList,
                            requirements: values.requirements || '-'
                        };
                        if (this.editingId) {
                            const index = this.superviseList.findIndex(item => item.id === this.editingId);
                            if (index > -1) {
                                this.superviseList.splice(index, 1, { ...this.superviseList[index], ...taskData });
                            }
                            this.$message.success('督办修改成功');
                        } else {
                            this.superviseList.unshift({ id: Date.now(), ...taskData });
                            this.$message.success('督办添加成功');
                        }
                        this.toggleSuperviseForm();
                        this.superviseLoading = false;
                    }, 500);
                }
            });
        },
        handleEditSupervise(record) {
            this.showSuperviseForm = true;
            this.editingId = record.id;
            const { typeValue, subTypeValue } = this.resolveTypeValue(record.type, record.subType);
            this.$nextTick(() => {
                this.superviseForm.setFieldsValue({
                    taskName: record.taskName,
                    deadline: moment(record.deadline),
                    type: typeValue ? (subTypeValue ? [typeValue, subTypeValue] : [typeValue]) : undefined,
                    requirements: record.requirements
                });
                if (record.executorList) {
                    this.executorList = record.executorList;
                }
            });
        },
        handleDeleteSupervise(id) {
            this.$confirm({
                title: '确定删除该督办吗?',
                content: '删除后不可恢复',
                onOk: () => {
                    this.superviseList = this.superviseList.filter(item => item.id !== id);
                    this.deletedSuperviseIds.push(id);
                    this.$message.success('删除成功');
                }
            });
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        async handleFileChange(e) {
            const files = Array.from(e.target.files || []);
            const newFiles = files.filter(file => !this.uploadFileList.some(f => f.name === file.name && f.size === file.size));
            if (!newFiles.length) { e.target.value = ''; return; }
            const data = new FormData();
            newFiles.forEach(file => data.append('files', file));
            try {
                const res = await uploadFile(data);
                if (Array.isArray(res)) {
                    this.uploadFileList = [...this.uploadFileList, ...res];
                }
            } catch (err) {
                console.error('上传文件失败', err);
                this.$message.error('文件上传失败');
            }
            e.target.value = '';
        },
        removeFile(index) {
            this.uploadFileList.splice(index, 1);
        },
        fileIcon(name) {
            const ext = name.split('.').pop().toLowerCase();
            if (['doc', 'docx'].includes(ext)) return 'file-word';
            if (['xls', 'xlsx'].includes(ext)) return 'file-excel';
            if (['ppt', 'pptx'].includes(ext)) return 'file-ppt';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return 'file-image';
            if (ext === 'pdf') return 'file-pdf';
            return 'file';
        },
        fileIconColor(name) {
            const ext = name.split('.').pop().toLowerCase();
            if (['doc', 'docx'].includes(ext)) return '#1890ff';
            if (['xls', 'xlsx'].includes(ext)) return '#52c41a';
            if (['ppt', 'pptx'].includes(ext)) return '#fa8c16';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return '#722ed1';
            if (ext === 'pdf') return '#f5222d';
            return '#8c8c8c';
        },
        formatFileSize(bytes) {
            if (!bytes) return '';
            const units = ['B', 'KB', 'MB', 'GB'];
            let i = 0;
            let size = bytes;
            while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
            return size.toFixed(1) + ' ' + units[i];
        },
        handleClose() {
            this.$close(this.$route.path);
        },
        async handleSaveAll() {
            this.saveLoading = true;
            try {
                const attendeeStateList = Object.entries(this.attendanceState).map(([id, state]) => ({
                    attendeeId: id,
                    attendanceState: state
                }));
                const minutesFileIds = this.uploadFileList.map(f => f.id).filter(Boolean).join(',');
                const superviseList = this.superviseList.map(item => ({
                    name: item.taskName,
                    originalDate: item.deadline,
                    type: item.type,
                    subType: item.subType,
                    remarks: item.requirements === '-' ? '' : item.requirements,
                    assignees: (item.executorList || []).map(u => ({
                        assigneeId: u.userId,
                        assigneeName: u.name,
                        assigneeDeptId: u.deptId || '',
                        assigneeDeptName: u.deptUniqueName || ''
                    }))
                }));
                await archiveMeeting({
                    id: this.detail.id,
                    attendeeStateList,
                    minutesFileIds,
                    superviseList,
                    deletedSuperviseIds: this.deletedSuperviseIds
                });
                this.$message.success('全部数据保存成功');
                this.$router.push('/oa/meeting');
            } catch (err) {
                console.error('归档保存失败', err);
                this.$message.error('保存失败');
            } finally {
                this.saveLoading = false;
            }
        }
    }
};
</script>

<style lang="less" scoped>
.meeting-detail-page {
  background: #f5f7fa;
  padding: 24px;
  min-height: 100vh;
}

.page-card {
  background: #fff;
  border-radius: 8px;
  padding: 24px 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

/* 基础区块标题 */
.section-block {
  margin-bottom: 8px;
}
.stats-text{
    margin-right: 30px;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  .bar {
    display: inline-block;
    width: 4px;
    height: 16px;
    background: #1890ff;
    border-radius: 2px;
    margin-right: 8px;
  }
}

/* 1. 顶部会议头 */
.header-section {
  display: flex;
  align-items: center;
  padding: 8px 0;
  .meeting-icon {
    width: 48px;
    height: 48px;
    background: #f0f5ff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
  }
  .meeting-info {
    .meeting-title {
      font-size: 18px;
      font-weight: 600;
      color: #1f1f1f;
    }
    .meeting-meta {
      color: #8c8c8c;
      font-size: 13px;
      margin-top: 4px;
    }
  }
}

/* 2. 参会人员 */
.attendee-table-wrapper {
  .user-cell {
    display: flex;
    align-items: center;
    .avatar-circle {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      margin-right: 8px;
    }
  }
  .status-tag {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 12px;
    font-size: 12px;
  }
  .status-confirmed { background: #f6ffed; color: #52c41a; border: 1px solid #b7eb8f; }
  .status-pending { background: #fffbe6; color: #faad14; border: 1px solid #ffe58f; }
  .status-rejected { background: #fff1f0; color: #ff4d4f; border: 1px solid #ffa39e; }
  .status-blue { background: #e6f7ff; color: #1890ff; border: 1px solid #91d5ff; }
}

/* 3. 会议纪要 */
.upload-area {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;

  .upload-trigger-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    .upload-tip { color: #8c8c8c; font-size: 12px; }
  }

  .file-list {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 12px;

    .file-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .file-info {
        display: flex;
        align-items: center;
        .file-name { font-weight: 500; margin-right: 12px; }
        .file-size { color: #8c8c8c; font-size: 12px; }
      }
      .delete-icon {
        cursor: pointer;
        &:hover { opacity: 0.7; }
      }
    }
  }

  .notify-checkbox {
    margin-top: 4px;
  }
}

/* 4. 督办发起 */
.supervise-form-wrapper {
  background: #f9fafc;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
  }
}

/* 底部按钮 */
.footer-actions {
  display: flex;
  justify-content: center;
  padding-top: 32px;
  gap: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 40px;

  button {
    min-width: 80px;
  }
}

/* AntD 表格微调覆盖 */
::v-deep .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
}

/* 开关选中绿色 */
::v-deep .ant-switch-checked {
  background-color: #52c41a;
}

.selected-user {
  position: relative;
  margin: 0 0 10px 0;
}

/* 人员选择器触发器 */
.emp-trigger {
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 0 11px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;

  &:hover { border-color: #1890ff; }
  &.has-val { background: #f0f5ff; border-color: #adc6ff; }

  .emp-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 12px;
    margin-right: 8px;
  }
  .emp-name { color: #1890ff; }
}
</style>